import type React from 'react';

import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  type SxProps,
  Typography,
} from '@mui/material';
import { useSetAtom } from 'jotai';
import { useEffectEvent as useEvent, useMemo, useState } from 'react';
import { type PropsWithChildren } from 'react';
import { matchPath, useLocation } from 'react-router';

import { clearCustomTableMemory } from '@/components/customTable/util/customTableMemory.util.ts';
import { themeColors } from '@/config/theme/themeColors.ts';
import { useNotificationsContext } from '@/contexts/notifications/Notifications.context.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import {
  SideBarListItemBadgeStyles,
  SideBarListItemButtonStyles,
} from '@/layouts/private/sidebar/SideBar.styles.ts';
import { ROUTES_PERMISSIONS, ROUTES_ROLES } from '@/layouts/routes/routesRoles.model.ts';
import { useOcdHistory } from '@/utils/useOcdHistory.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { isPrivacyPolicySigningRequired } from '@/views/signPrivacyPolicy/util/isPrivacyPolicySigningRequired.ts';
import { UserProfileLanguageAtom } from '@/views/userProfile/model/UserProfileLanguage.atom.ts';

import { RouteEnum } from '../../../../routes/Route.model.ts';
import { hasAccessByPermissionCheck, isAuthorized } from '../../../authorization.ts';
import {
  MENU_GROUPS,
  type MenuGroupModel,
  type MenuItemModel,
} from './model/SideBarMenuItems.model.tsx';

interface SideBarMenuProps {
  showExpandedContent: boolean;
}

const SideBarMenu: React.FC<SideBarMenuProps> = ({ showExpandedContent }) => {
  const {
    user: { permissions, role },
  } = useUserContext();

  const filteredGroups = MENU_GROUPS.map(group => ({
    ...group,
    items: group.items.filter(
      ({ route }) =>
        isAuthorized(ROUTES_ROLES[route], role) &&
        hasAccessByPermissionCheck(ROUTES_PERMISSIONS[route], permissions),
    ),
  })).filter(group => group.items.length > 0);

  return (
    <List sx={{ mt: 3 }}>
      {filteredGroups.map((group, groupIndex) => (
        <MenuGroup
          group={group}
          isFirstGroup={groupIndex === 0}
          key={group.id}
          showExpandedContent={showExpandedContent}
        />
      ))}
    </List>
  );
};

interface MenuGroupProps {
  group: MenuGroupModel;
  isFirstGroup: boolean;
  showExpandedContent: boolean;
}

const MenuGroup: React.FC<MenuGroupProps> = ({ group, isFirstGroup, showExpandedContent }) => {
  const { t } = useTranslationWithPrefix('menu.groups');
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const hasActiveItem = useMemo(
    () => group.items.some(item => !!matchPath(pathname, item.route)),
    [group.items, pathname],
  );

  const isExpanded = isOpen || hasActiveItem;

  const handleToggle = () => setIsOpen(prev => !prev);

  if (!showExpandedContent) {
    return (
      <>
        {group.items.map(menuItem => (
          <ListItemContainer
            data={menuItem}
            key={menuItem.id}
            showExpandedContent={showExpandedContent}
          />
        ))}
      </>
    );
  }

  return (
    <Box sx={{ pt: isFirstGroup ? 0 : 2 }}>
      <Box
        id={`sidebar-group-${group.id}`}
        onClick={handleToggle}
        sx={GroupStyles({ hasActiveItem, isFirstGroup })}
      >
        <Typography
          sx={{
            fontSize: 'inherit',
            fontWeight: 'inherit',
            letterSpacing: 'inherit',
            lineHeight: 'inherit',
            textTransform: 'inherit',
          }}
          variant="overline"
        >
          {t(group.id)}
        </Typography>
        <Box
          sx={{
            transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 0.2s ease-in-out',
          }}
        >
          <ExpandMoreIcon sx={{ fontSize: '0.875rem', opacity: 0.7 }} />
        </Box>
      </Box>
      <Collapse in={isExpanded} timeout={200}>
        <Box>
          {group.items.map(menuItem => (
            <ListItemContainer
              data={menuItem}
              key={menuItem.id}
              showExpandedContent={showExpandedContent}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

interface ListItemContainerProps {
  data: MenuItemModel;
  showExpandedContent: boolean;
}

const ListItemContainer: React.FC<ListItemContainerProps> = ({ data, showExpandedContent }) => {
  const { pathname } = useLocation();
  const { menuBadges } = useNotificationsContext();
  const isSelected = !!matchPath(pathname, data.route);
  const {
    user: { initializationDone, privacyPolicyStatus },
  } = useUserContext();
  const privacyPolicySigningRequired = isPrivacyPolicySigningRequired(privacyPolicyStatus);
  const isMenuDisabled = !initializationDone || privacyPolicySigningRequired;

  const badgesCount = useMemo(() => {
    if (!data.badgeType) {
      return 0;
    }

    return menuBadges[data.badgeType]?.length ?? 0;
  }, [data, menuBadges]);

  return (
    <ListItemButton
      component={'div'}
      disabled={isMenuDisabled}
      disableGutters
      id={data.id}
      nativeButton={false}
      selected={isSelected}
      sx={SideBarListItemButtonStyles}
    >
      <OcdRouterLink to={data}>
        <MenuItemContent
          badgesCount={badgesCount}
          data={data}
          showExpandedContent={showExpandedContent}
        />
      </OcdRouterLink>
    </ListItemButton>
  );
};

interface MenuItemContentProps {
  badgesCount: number;
  data: MenuItemModel;
  showExpandedContent: boolean;
}

const MenuItemContent: React.FC<MenuItemContentProps> = ({
  badgesCount,
  data,
  showExpandedContent,
}) => {
  const { t } = useTranslationWithPrefix('menu');
  const {
    user: { role },
  } = useUserContext();

  const isBadgeVisible = data.badgeFor === undefined || isAuthorized(data.badgeFor, role);

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        height: '48px',
        justifyContent: showExpandedContent ? 'flex-start' : 'center',
        width: '100%',
      }}
    >
      <ListItemIcon sx={IconStyles({ showExpandedContent })}>{data.icon}</ListItemIcon>
      <Box sx={ItemStyles({ showExpandedContent })}>
        <ListItemText
          primary={<Typography variant="f12Regular">{t(data.id)}</Typography>}
          sx={{
            alignItems: 'center',
            display: 'flex',
            height: '48px',
            minWidth: 0,
            whiteSpace: 'normal',
          }}
        />
        {isBadgeVisible && badgesCount > 0 && (
          <ErrorIcon color="warning" sx={SideBarListItemBadgeStyles} />
        )}
      </Box>
    </Box>
  );
};

interface OcdRouterLinkProps extends PropsWithChildren {
  className?: string;
  to: MenuItemModel;
}

const OcdRouterLink: React.FC<OcdRouterLinkProps> = props => {
  const { children, className, to } = props;
  const { push } = useOcdHistory();
  const {
    user: { userProfileId },
  } = useUserContext();
  const setProfileLanguage = useSetAtom(UserProfileLanguageAtom);

  const handleClick = useEvent(() => {
    clearCustomTableMemory();
    const { route } = to;
    if (route === RouteEnum.USER_PROFILE) {
      setProfileLanguage(cur => ({ ...cur, shouldClear: true }));
      if (!userProfileId) {
        push(route);
        return;
      }
      push({
        params: { userProfileId: userProfileId ?? '' },
        pathname: RouteEnum.USER_PROFILE,
      });
    } else {
      push(route);
    }
  });

  return (
    <Box className={className} onClick={handleClick} sx={{ minWidth: 0, width: '100%' }}>
      {children}
    </Box>
  );
};

export default SideBarMenu;

const GroupStyles = (params: { hasActiveItem: boolean; isFirstGroup: boolean }): SxProps => ({
  '&:hover': {
    color: 'rgba(255, 255, 255, 0.9)',
  },
  alignItems: 'center',
  color: params.hasActiveItem ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.6)',
  cursor: 'pointer',
  display: 'flex',
  fontSize: '0.75rem',
  fontWeight: 600,
  justifyContent: 'space-between',
  letterSpacing: '0.08rem',
  lineHeight: 1.2,
  pb: 1,
  pt: params.isFirstGroup ? 0 : 1,
  px: 2,
  textTransform: 'uppercase',
  transition: 'color 0.2s ease-in-out',
});

const IconStyles = (params: { showExpandedContent: boolean }): SxProps => ({
  '.Mui-selected &': {
    '& svg': {
      fill: themeColors.turquoise,
    },
  },
  alignItems: 'center',
  color: themeColors.grey,
  display: 'flex',
  justifyContent: 'center',
  minWidth: params.showExpandedContent ? '5px' : 'auto',
  pr: params.showExpandedContent ? 4 : 0,
  transition: 'all 0.2s ease-in-out',
});

const ItemStyles = (params: { showExpandedContent: boolean }): SxProps => ({
  alignItems: 'center',
  display: 'flex',
  flex: params.showExpandedContent ? 1 : '0 0 auto',
  height: '48px',
  minWidth: 0,
  opacity: params.showExpandedContent ? 1 : 0,
  overflow: 'hidden',
  transform: params.showExpandedContent ? 'translateX(0)' : 'translateX(-10px)',
  transition: params.showExpandedContent
    ? 'opacity 0.3s ease-out 0.1s, transform 0.3s ease-out 0.1s, width 0.3s ease-out'
    : 'opacity 0.2s ease-in, transform 0.2s ease-in, width 0.3s ease-out 0.1s',
  width: params.showExpandedContent ? 'auto' : 0,
});
